import Link from 'client/components/Link';

export default function TermsContent() {
  return (
    <div className="Legal-content">
      <h1>Eth.id Terms of Service</h1>
      <p>
        <b>Last updated: June 25, 2022</b>
      </p>

      <p>
        These Terms of Service (<b>“Terms”</b>) apply to your access to and use
        of the Services (as defined in Section 1 below) provided by Eth Id, Inc.
        d/b/a Eth.id, a Delaware corporation (Eth Id, Inc., together with its
        affiliates and subsidiaries or “<b>we</b>
        ”).{' '}
        <b>
          By clicking “I Accept” or by accessing or using our Services, you
          agree to these Terms, including the mandatory arbitration provision
          and class action waiver in Section 17 and the provisions relating to
          future modification, termination and migration of our Services in
          Section 19. If you do not agree to these Terms, do not use our
          Services.
        </b>
      </p>
      <p>
        If you have any questions about these Terms or our Services, please
        contact us at support@eth.id. For information about how we collect, use,
        share and otherwise process information about you, please see our
        <Link to="/privacy"> Privacy Policy</Link>.
      </p>

      <p>You and Company agree as follows:‍</p>
      <ol>
        <li>
          <p>
            <b>Overview and Scope</b>
          </p>

          <p>
            Company has developed a registry of names that is accessible through
            the Company’s website (the “Platform”). People who use the Services
            may license a unique name (each a “domain”) from the Company and
            associate it on the Platform with a third-party website URL, social
            media handle, cryptocurrency payment address, email address,
            location data, text data, or other online data selected by the user
            (the “Associated Data”) in accordance with these Terms.
          </p>

          <p>
            These Terms govern the services (the “Services”) that Company makes
            available to Platform users, including use or access to the Eth.id
            Registry, Platform and Discord server, the ability to license and
            associate a Eth.id domain with Associated Data, resolve queries of a
            Eth.id domain on the Eth.id Registry to the Associated Data, and
            obtain related services through the Platform. All references to
            Services in these Terms include Eth.id domains unless otherwise
            specified.
          </p>
        </li>

        <li>
          <p>
            <b>Eligibility</b>
          </p>

          <ol>
            <li>
              You must be at least 16 years of age to use our Services. If you
              are under 18 years of age (or the age of legal majority where you
              live), you may use our Services only under the supervision of a
              parent or legal guardian who agrees to be bound by these Terms. If
              you are a parent or legal guardian of a user under the age of 18
              (or the age of legal majority), you agree to be fully responsible
              for the acts or omissions of such user in relation to our
              Services. If you use our Services on behalf of another person or
              entity, (i) all references to “you” throughout these Terms will
              include that person or entity; (ii) you represent that you are
              authorized to accept these Terms on that person’s or entity’s
              behalf; and (iii) in the event you or the person or entity
              violates these Terms, the person or entity agrees to be
              responsible to us.
            </li>
            <li>You may not access or use our Services if:</li>
            <ul>
              <li>you have been suspended from using our Services;</li>
              <li>
                under the applicable law of the jurisdiction(s) in which you
                reside or conduct business, you are prohibited from using the
                Services or do not have the requisite licenses or other
                governmental authorizations to use the Services;
              </li>
              <li>
                you are located in a country that is subject to a U.S.
                government embargo or that has been designated by the U.S.
                government as a “terrorist supporting” country;
              </li>
              <li>
                you are listed on any U.S. government list of prohibited or
                restricted parties; or
              </li>
              <li>
                your use of the Services breaches any other agreement to which
                you are a party.‍
              </li>
            </ul>
          </ol>
        </li>

        <li>
          <p>
            <b>User Accounts and Account Security</b>
          </p>
          <ol>
            <li>
              You need to register for an account to access some or all of our
              Services, including to purchase a Eth.id domain license and
              associate your Eth.id domain with Associated Data. If you register
              for an account, you must provide accurate account information and
              promptly update this information if it changes. You are
              responsible for the activities that occur in connection with your
              account and must maintain the security of your account. You are
              prohibited from sharing your password or other log-in credentials
              with any other person. Promptly notify us if you discover or
              suspect that someone has accessed your account without your
              permission.
            </li>
            <li>
              We can terminate or suspend your account at any time at our
              discretion. We are not responsible for any loss or harm related to
              your inability to access or use the Services. You may not bring a
              claim against us for suspending or terminating another person’s
              account, and you agree you will not bring such a claim. If you try
              to bring such a claim, you are responsible for the damages caused,
              including attorneys’ fees and costs.
            </li>
            <li>
              You agree that your account is not transferable and that in the
              event of your death, incapacity or unavailability, we may
              terminate any rights to your account and Eth.id domains.
            </li>
          </ol>
        </li>
        <li>
          <p>
            <b>Purchasing a Eth.id Domain License</b>
          </p>
          <p>
            This Section 4 only applies if you purchase a Eth.id domain license
            as part of our Services.
          </p>
          <ol>
            <li>
              To purchase a Eth.id domain license, you need to place an order
              for your desired Eth.id domain license through your account. You
              agree to pay all fees due for a Eth.id domain license at the time
              you order the Eth.id domain license, according to Company’s
              current Eth.id domain license fee schedule.
            </li>
            <li>
              You may purchase a Eth.id domain license by entering digital
              assets “<b>Payment Method</b>” in your account. If for any reason
              Company is not able to take the payment from the Payment Method in
              your account when you purchase a Eth.id domain license, your
              Eth.id domain license will expire.
            </li>
            <li>
              Company may modify its Eth.id domain license fee schedule at any
              time, and modifications will be posted on the Platform and
              effective immediately with respect to future Eth.id domain license
              purchases without further notice. All Eth.id domain license
              purchases will be subject to these Terms, including the limited
              license set forth in Section 7, and will be non-refundable unless
              otherwise agreed.
            </li>
            <li>
              Company may collect a fee and/or commission (each, a “Transaction
              Fee”) on the total value of any Eth.id domain transaction you
              undertake with your Eth.id domain. The applicable Transaction Fee
              will be determined by Company from time to time in its sole
              discretion and, if applicable, will be communicated to Eth.id
              domain owners at least 30 days prior to going into effect.
            </li>
            <li>
              Other Digital Assets. From time to time, Eth.id may offer licenses
              consisting of other digital assets (“
              <b>Eth.id items</b>”) that Eth.id may issue to users of the
              Services. Eth.id items are offered and sold subject to the terms
              of sale (if any) specified at the time of sale.
            </li>
            <li>
              <b>
                Except as required by law, all Eth.id domain license purchases
                are final and non-refundable. No refunds, returns or exchanges
                will be permitted for any reason. ALL SALES ARE FINAL.
              </b>
            </li>
          </ol>
        </li>
        <li>
          <p>
            <b>Associated Data</b>
          </p>
          <ol>
            <li>
              Our Services allow you and other users to associate a Eth.id
              domain with Associated Data through your account. Except for the
              license you grant below, you retain all rights in and to the
              Associated Data that you associate with your Eth.id domain, as
              between you and Company.
            </li>
            <li>
              You grant Company and its subsidiaries and affiliates a
              nonexclusive, royalty-free, worldwide, fully paid, and
              sublicensable license to use, reproduce, modify, adapt, publish,
              translate, create derivative works from, distribute, publicly
              perform and display your Associated Data and any name, username or
              likeness provided in connection with your Associated Data in all
              media formats and channels now known or later developed without
              compensation to you. When you associate or otherwise share your
              Associated Data on or through our Services, you understand that
              your Associated Data may be visible to others.
            </li>
            <li>
              You may not associate or otherwise share any Associated Data that
              violates these Terms, that is confidential, or for which you do
              not have all the rights necessary to disclose and to grant us the
              license described above. In addition to the requirements in
              Section 6(b) below, you represent and warrant that your Associated
              Data, and our use of such Associated Data as permitted by these
              Terms, will not violate any rights of or cause injury to any
              person or entity. Although we have no obligation to screen, edit
              or monitor Associated Data, we may delete or remove your
              Associated Data at any time and for any reason with or without
              notice.
            </li>
          </ol>
        </li>
        <li>
          <p>
            <b>Prohibited Conduct and Associated Data</b>
          </p>
          <ol>
            <li>
              You are solely responsible for your conduct while using or
              accessing our Services. While using or accessing our Services, you
              will not:
              <ul>
                <li>
                  Violate any applicable law, contract, intellectual property
                  right or other third-party right or commit a tort;
                </li>
                <li>
                  Use our Services or for any illegal or unauthorized purpose,
                  or engage in, encourage or promote any activity that violates
                  these Terms;
                </li>
                <li>
                  Attempt to circumvent any content-limiting techniques we
                  employ;
                </li>
                <li>
                  Use or attempt to use another user’s account unless authorized
                  to do so by that user and Company;
                </li>
                <li>
                  Impersonate or post Associated Data on behalf or any person or
                  entity or otherwise misrepresent your affiliation with a
                  person or entity;
                </li>
                <li>
                  Use our Services other than for their intended purpose and in
                  any manner that could interfere with, disrupt, negatively
                  affect or inhibit other users from fully enjoying our Services
                  or that could damage, disable, overburden or impair the
                  functioning of our Services in any manner;
                </li>
                <li>
                  Reverse engineer any aspect of our Services or do anything
                  that might discover source code or bypass or circumvent
                  measures employed to prevent or limit access to any part of
                  our Services;
                </li>
                <li>
                  Use any data mining, robots or similar data gathering or
                  extraction methods designed to scrape or extract data from our
                  Services;
                </li>
                <li>
                  Develop or use any applications that interact with our
                  Services without our prior written consent;
                </li>
                <li>
                  Use our Services for benchmarking purposes or for the purpose
                  of developing a competitive product;
                </li>
                <li>
                  Manipulate, or attempt to manipulate, our Services in any way;
                </li>
                <li>
                  Send, distribute or post spam, unsolicited or bulk commercial
                  electronic communications, chain letters, or pyramid schemes;
                </li>
                <li>
                  Bypass or ignore instructions contained in our robots.txt
                  file;
                </li>
                <li>
                  Engage in any harassing, threatening, intimidating, predatory
                  or stalking conduct; or
                </li>
                <li>
                  Engage in any conduct that, in our sole judgment, is
                  objectionable, restricts or inhibits any other person from
                  using or enjoying our Services, or may expose Company or
                  others to any harm or liability of any type.
                </li>
              </ul>
            </li>
            <li>
              You may not associate any Associated Data with your Eth.id domain
              that:
              <ul>
                <li>
                  Is unlawful provided, however, we neither review nor evaluate
                  the content hosted by the third party websites (“
                  <b>Third Party Sites</b>”) whose URLs you may associate with
                  your Eth.id domain and assume no liability or responsibility
                  for the content of such Third Party Sites;
                </li>
                <li>
                  Would constitute, encourage or provide instructions for a
                  criminal offense, violate the rights of any party or otherwise
                  create liability or violate any law;
                </li>
                <li>
                  May infringe any patent, trademark, trade secret, copyright or
                  other intellectual or proprietary right of any party;
                </li>
                <li>
                  Contains or depicts any statements, remarks or claims that do
                  not reflect your honest views and experiences;
                </li>
                <li>
                  Impersonates, or misrepresents your affiliation with, any
                  person or entity;
                </li>
                <li>
                  Contains any unsolicited promotions, political campaigning,
                  advertising or solicitations;
                </li>
                <li>
                  Contains any private or personal information of a third party
                  without such third party’s consent;
                </li>
                <li>
                  Contains any viruses, corrupted data or other harmful,
                  disruptive or destructive files or content; or
                </li>
                <li>
                  In our sole judgment, is objectionable, restricts or inhibits
                  any other person from using or enjoying our Services, or may
                  expose Company or others to any harm or liability of any type.
                </li>
              </ul>
            </li>
            <li>
              Enforcement of this Section 6 is solely at Company&lsquo;s
              discretion, and failure to enforce this section in some instances
              does not constitute a waiver of our right to enforce it in other
              instances.In addition, this Section 6 does not create any private
              right of action on the part of any third party or any reasonable
              expectation that the Services will not contain any content that is
              prohibited by such rules.
            </li>
          </ol>
        </li>
        <li>
          <p>
            <b>Ownership; Limited Licenses</b>
          </p>
          <ol>
            <li>
              The Services (which includes and any constituent part thereof
              including any Eth.id domains, text, graphics, images, photographs,
              videos, illustrations and other content contained therein), are
              owned by Company or our licensors and are protected under both
              United States and foreign laws. Except as explicitly stated in
              these Terms, all rights in and to the Services are reserved by us
              or our licensors.
            </li>
            <li>
              Subject to your compliance with these Terms, you are hereby
              granted a limited, nonexclusive, non-sublicensable,
              non-transferable, revocable license to access and use our Services
              (other than Eth.id domains) for your own personal, noncommercial
              use.
            </li>
            <li>
              If you purchase a license to a Eth.id domain in accordance with
              Section 4, and subject to your compliance with these Terms, you
              are hereby granted a limited, exclusive, non-sublicensable,
              non-transferable, revocable license to access, copy, display,
              distribute and use the Services solely for the purpose of allowing
              third-parties to perform lookups of Associated Data associated to
              such Eth.id domain via the Company’s application programming
              interface.
            </li>
            <li>
              Without limiting the foregoing provisions in this Section 7, you
              will not, directly or indirectly: (i) modify or create derivative
              works of the Services in whole or in part; (ii) rent, lease, lend,
              sell, advertise, assign, encumber, or otherwise commercially use
              the Services; (iii) remove any proprietary notices from the
              Services; or (iv) use the Services in any manner or for any
              purpose that infringes, misappropriates, or otherwise violates any
              intellectual property right or other right of the Company or any
              other person, or that violates any applicable law.
            </li>
            <li>
              Any use of the Services other than as specifically authorized
              herein, without our prior written permission, is (i) strictly
              prohibited; (ii) will immediately terminate the license for
              Services (other than Eth.id domains) in Section 7(b) or the
              license for Eth.id domains in Section 7(c), as applicable; and
              (iii) violate our intellectual property rights. If your Eth.id
              domain license is terminated, you will immediately lose access to
              your Eth.id domain.‍
            </li>
          </ol>
        </li>
        <li>
          <p>
            <b>Trademarks</b>
          </p>
          <p>
            Eth Id, Inc. d/b/a Eth.id, the Eth.id mark and our logos, our
            product or service names, our slogans and the look and feel of the
            Services are trademarks of Company and may not be copied, imitated
            or used, in whole or in part, without our prior written permission.
            All other trademarks, registered trademarks, product names and
            company names or logos mentioned on the Services are the property of
            their respective owners. Reference to any products, services,
            processes or other information by trade name, trademark,
            manufacturer, supplier or otherwise does not constitute or imply
            endorsement, sponsorship or recommendation by us.
          </p>
        </li>
        <li>
          <p>
            <b>Feedback</b>
          </p>
          <p>
            You may voluntarily post, submit or otherwise communicate to us any
            questions, comments, suggestions, ideas, original or creative
            materials or other information about Company or our Services
            (collectively, “<b>Feedback</b>”). You understand that we may use
            such Feedback for any purpose, commercial or otherwise, without
            acknowledgment or compensation to you, including to develop, copy,
            publish, or improve the Feedback in Company’s sole discretion. You
            understand that Company may treat Feedback as nonconfidential.
          </p>
        </li>
        <li>
          <p>
            <b>Repeat Infringer Policy; Copyright Complaints</b>
          </p>
          <p>
            In accordance with the Digital Millennium Copyright Act and other
            applicable law, we have adopted a policy of terminating, in
            appropriate circumstances, the accounts of users who repeatedly
            infringe the intellectual property rights of others. If you believe
            that anything on our Services infringes any copyright that you own
            or control, you may notify Company’s designated agent as follows:
          </p>
          <div className="Legal-address">
            <p>
              Eth Id, Inc. <br />
              Attn: Legal Department <br />
              1032 E Brandon Blvd #2002 <br />
              Brandon, FL 33511 <br />
              Email: legal@eth.id
            </p>
          </div>
          <p>
            Please see{' '}
            <a href="https://www.govinfo.gov/content/pkg/USCODE-2011-title17/pdf/USCODE-2011-title17-chap5-sec512.pdf">
              17 U.S.C. § 512(c)(3)
            </a>{' '}
            for the requirements of a proper notification. Also, please note
            that if you knowingly misrepresent that any activity or material on
            our Services is infringing, you may be liable to Company for certain
            costs and damages.
          </p>
        </li>
        <li>
          <p>
            <b>Third-Party Content</b>
          </p>
          <p>
            We may provide information about third-party products, services,
            activities or events, or we may allow third parties to make their
            content and information available on or through the Services
            (collectively, “<b>Third-Party Content</b>”). We provide Third-Party
            Content as a service to those interested in such content. Your
            dealings or correspondence with third parties and your use of or
            interaction with any Third-Party Content are solely between you and
            the third party. Company does not control or endorse, and makes no
            representations or warranties regarding, any Third-Party Content,
            and your access to and use of such Third-Party Content is at your
            own risk.
          </p>
        </li>
        <li>
          <p>
            <b>Indemnification</b>
          </p>
          <p>
            To the fullest extent permitted by applicable law, you will
            indemnify, defend and hold harmless Company, and our past, present
            and future employees, officers, directors, contractors, consultants,
            equity holders, suppliers, vendors, service providers, parent
            companies, subsidiaries, affiliates, agents, representatives,
            predecessors, successors and assigns (individually and collectively,
            the “<b>Company Parties</b>”) from and against any losses,
            liabilities, claims, demands, actions, damages, expenses or costs (“
            <b>Claims</b>”) arising out of or related to (a) your access to or
            use of the Services; (b) your Associated Data or Feedback; (c) your
            violation of these Terms; (d) your violation, misappropriation or
            infringement of any rights of another (including intellectual
            property rights or privacy rights); or (e) your conduct in
            connection with the Services. You agree to promptly notify Company
            Parties of any third-party Claims, cooperate with Company Parties in
            defending such Claims and pay all fees, costs and expenses
            associated with defending such Claims (including attorneys&lsquo;
            fees). You also agree that the Company Parties will have control of
            the defense or settlement, at Company&lsquo;s sole option, of any
            third-party Claims. This indemnity is in addition to, and not in
            lieu of, any other indemnities set forth in a written agreement
            between you and Company or the other Company Parties.
          </p>
        </li>
        <li>
          <p>
            <b>Disclaimers</b>
          </p>
          <p>
            <b>
              Your use of our Services is at your sole risk. Except as otherwise
              provided in a writing by us, our Services and any content therein
              are provided “as is” and “as available” without warranties of any
              kind, either express or implied, including implied warranties of
              merchantability, fitness for a particular purpose, title, and
              non-infringement. In addition, Company does not represent or
              warrant that our Services are accurate, complete, reliable,
              current or error-free. While Company attempts to make your use of
              our Services and any content therein safe, we cannot and do not
              represent or warrant that our Services or servers are free of
              malware, viruses or other harmful components. You assume the
              entire risk as to the quality and performance of the Services.
            </b>
          </p>
          <p>
            Some jurisdictions do not allow the exclusion of certain warranties
            or disclaimer of implied terms in contracts with consumers, so some
            or all of the exclusions of warranties and disclaimers in this
            Section 13 may not apply to you.
          </p>
        </li>

        <li>
          <p>
            <b>Limitation of Liability</b>
          </p>
          <ol>
            <li>
              <b>
                To the fullest extent permitted by applicable law, Company and
                the other Company Parties will not be liable to you under any
                theory of liability—whether based in contract, tort, negligence,
                strict liability, warranty, or otherwise—for any indirect,
                consequential, exemplary, incidental, punitive or special
                damages or lost profits, even if Company or the other Company
                Parties have been advised of the possibility of such damages.
              </b>
            </li>
            <li>
              <b>
                The total liability of Company and the other Company Parties for
                any claim arising out of or relating to these Terms or our
                Services, regardless of the form of the action, is limited to
                the amount paid to the Company by you to use our Services.
              </b>
            </li>
            <li>
              The limitations set forth in this Section 14 will not limit or
              exclude liability for the gross negligence, fraud or intentional
              misconduct of Company or the other Company Parties or for any
              other matters in which liability cannot be excluded or limited
              under applicable law. Additionally, some jurisdictions do not
              allow the exclusion or limitation of incidental or consequential
              damages, so the above limitations or exclusions may not apply to
              you.
            </li>
          </ol>
        </li>
        <li>
          <p>
            <b>Release</b>
          </p>

          <p>
            To the fullest extent permitted by applicable law, you release
            Company and the other Company Parties from responsibility,
            liability, claims, demands and/or damages (actual and consequential)
            of every kind and nature, known and unknown (including claims of
            negligence), arising out of or related to (a) disputes between you
            and other users of the Services; (b) disputes between you and third
            parties that view, access, use, host or otherwise interact with your
            Eth.id domain or Associated Data; and (c) the acts or omissions of
            third parties.{' '}
            <b>
              You waive any statute or common law principles that would
              otherwise limit the coverage of this release to include only those
              claims which you may know or suspect to exist in your favor at the
              time of agreeing to this release.
            </b>
          </p>
        </li>
        <li>
          <p>
            <b>Transfer and Processing Data</b>
          </p>
          <p>
            In order for us to provide our Services, you agree that we may
            process, transfer and store information about you in the United
            States and other countries, where you may not have the same rights
            and protections as you do under local law.
          </p>
        </li>
        <li>
          <p>
            <b>Dispute Resolution; Binding Arbitration</b>
          </p>

          <p>
            <b>
              Please read the following section carefully because it requires
              you to arbitrate certain disputes and claims with Company and
              limits the manner in which you can seek relief from us, unless you
              opt out of arbitration by following the instructions set forth
              below. No class or representative actions or arbitrations are
              allowed under this arbitration provision. In addition, arbitration
              precludes you from suing in court or having a jury trial.
            </b>
          </p>

          <ol>
            <li>
              <b>
                No Representative Actions. You and Company agree that any
                dispute arising out of or related to these Terms or our Services
                is personal to you and Company and that any dispute will be
                resolved solely through individual action, and will not be
                brought as a class arbitration, class action or any other type
                of representative proceeding.
              </b>
            </li>
            <li>
              <b>Arbitration of Disputes.</b> Except for (i) small claims
              disputes in which you or Company seeks to bring an individual
              action in small claims court located in the county of your billing
              address or (ii) disputes in which you or Company seeks injunctive
              or other equitable relief (x) to enforce this dispute resolution
              clause or (y) for the alleged infringement or misappropriation of
              intellectual property, including copyrights, trademarks, trade
              names, logos, trade secrets or patents,{' '}
              <b>
                you and Company waive your rights to a jury trial and to have
                any other dispute arising out of or related to these Terms or
                our Services, including claims related to privacy and data
                security, (collectively, “Disputes”) resolved in court.
              </b>{' '}
              Instead, for any Dispute that you have against Company you agree
              to first contact Company and attempt to resolve the claim
              informally by sending a written notice of your claim (“
              <b>Notice</b>”) to Company by email at support@eth.id or by
              certified mail addressed to 1032 E Brandon Blvd #2002, Brandon, FL
              33511. The Notice must (I) include your name, residence address,
              email address, and telephone number; (II) describe the nature and
              basis of the Dispute; and (III) set forth the specific relief
              sought. Our notice to you will be similar in form to that
              described above. If you and Company cannot reach an agreement to
              resolve the Dispute within thirty (30) days after such Notice is
              received, then either party may submit the Dispute to binding
              arbitration administered by JAMS or, under the limited
              circumstances set forth above, in court. All Disputes submitted to
              JAMS will be resolved through confidential, binding arbitration
              before one arbitrator. Arbitration proceedings will be held in
              Miami, Florida unless you are a consumer, in which case you may
              elect to hold the arbitration in your county of residence. For
              purposes of this Section 17, a “<b>consumer</b>” means a person
              using the Services for personal, family or household purposes.You
              and Company agree that Disputes will be held in accordance with
              the JAMS Streamlined Arbitration Rules and Procedures (“
              <b>JAMS Rules</b>”). The most recent version of the JAMS Rules are
              available on the{' '}
              <a href="https://www.jamsadr.com/rules-streamlined-arbitration/">
                JAMS website
              </a>{' '}
              and are hereby incorporated by reference. You either acknowledge
              and agree that you have read and understand the JAMS Rules or
              waive your opportunity to read the JAMS Rules and waive any claim
              that the JAMS Rules are unfair or should not apply for any reason.
            </li>
            <li>
              You and Company agree that these Terms affect interstate commerce
              and that the enforceability of this Section 17 will be
              substantively and procedurally governed by the Federal Arbitration
              Act, 9 U.S.C. § 1, et seq. (the “<b>FAA</b>”), to the maximum
              extent permitted by applicable law. As limited by the FAA, these
              Terms and the JAMS Rules, the arbitrator will have exclusive
              authority to make all procedural and substantive decisions
              regarding any Dispute and to grant any remedy that would otherwise
              be available in court. The arbitrator may conduct only an
              individual arbitration and may not consolidate more than one
              individual’s claims, preside over any type of class or
              representative proceeding or preside over any proceeding involving
              more than one individual.
            </li>
            <li>
              The arbitration will allow for the discovery or exchange of
              non-privileged information relevant to the Dispute.The arbitrator,
              Company, and you will maintain the confidentiality of any
              arbitration proceedings, judgments and awards, including
              information gathered, prepared and presented for purposes of the
              arbitration or related to the Dispute(s) therein. The arbitrator
              will have the authority to make appropriate rulings to safeguard
              confidentiality, unless the law provides to the contrary. The duty
              of confidentiality does not apply to the extent that disclosure is
              necessary to prepare for or conduct the arbitration hearing on the
              merits, in connection with a court application for a preliminary
              remedy or in connection with a judicial challenge to an
              arbitration award or its enforcement, or to the extent that
              disclosure is otherwise required by law or judicial decision.
            </li>
            <li>
              You and Company agree that for any arbitration you initiate, you
              will pay the filing fee (up to a maximum of $250 if you are a
              consumer), and Company will pay the remaining JAMS fees and costs.
              For any arbitration initiated by Company, Company will pay all
              JAMS fees and costs. You and Company agree that the state or
              federal courts of the State of Florida and the United States
              sitting in Miami, Florida have exclusive jurisdiction over any
              appeals and the enforcement of an arbitration award.
            </li>
            <li>
              <b>
                Any Dispute must be filed within one year after the relevant
                claim arose; otherwise, the Dispute is permanently barred, which
                means that you and Company will not have the right to assert the
                claim.
              </b>
            </li>
            <li>
              <b>
                You have the right to opt out of binding arbitration within 30
                days of the date you first accepted the terms of this Section 17
                by sending a written notice to the Company by email at
                support@eth.id or by certified mail addressed to 1032 E Brandon
                Blvd #2002, Brandon, FL 33511.
              </b>{' '}
              In order to be effective, the opt-out notice must include your
              full name and address and clearly indicate your intent to opt out
              of binding arbitration. By opting out of binding arbitration, you
              are agreeing to resolve Disputes in accordance with Section 17.
            </li>
            <li>
              If any portion of this Section 17 is found to be unenforceable or
              unlawful for any reason, (i) the unenforceable or unlawful
              provision shall be severed from these Terms; (ii) severance of the
              unenforceable or unlawful provision shall have no impact
              whatsoever on the remainder of this Section 17 or the parties’
              ability to compel arbitration of any remaining claims on an
              individual basis pursuant to this Section 17; and (iii) to the
              extent that any claims must therefore proceed on a class,
              collective, consolidated, or representative basis, such claims
              must be litigated in a civil court of competent jurisdiction and
              not in arbitration, and the parties agree that litigation of those
              claims shall be stayed pending the outcome of any individual
              claims in arbitration. Further, if any part of this Section 17 is
              found to prohibit an individual claim seeking public injunctive
              relief, that provision will have no effect to the extent such
              relief is allowed to be sought out of arbitration, and the
              remainder of this Section 17 will be enforceable.
            </li>
          </ol>
        </li>
        <li>
          <p>
            <b>Governing Law and Venue</b>
          </p>

          <p>
            Any dispute arising from these Terms and your use of the Services
            will be governed by and construed and enforced in accordance with
            the laws of Florida, except to the extent preempted by U.S. federal
            law, without regard to conflict of law rules or principles (whether
            of Florida or any other jurisdiction) that would cause the
            application of the laws of any other jurisdiction. Any dispute
            between the parties that is not subject to arbitration or cannot be
            heard in small claims court will be resolved in the state or federal
            courts of Florida and the United States, respectively, sitting in
            Miami, Florida.
          </p>
        </li>
        <li>
          <p>
            <b>Modifying and Terminating our Services; Service Migration</b>
          </p>

          <b>
            <ol>
              <li>
                We reserve the right to modify our Services or to suspend or
                stop providing all or portions of our Services at any time. You
                also have the right to stop using our Services at any time. We
                are not responsible for any loss or harm related to your
                inability to access or use our Services. If we discontinue
                providing all or portions of the Services, we will, where
                reasonably possible, give you advance notice.
              </li>
              <li>
                We reserve the right to migrate all or any part of the Services
                from a centralized directory maintained by Company or an
                affiliate to a decentralized directory maintained on a
                distributed ledger of which Company has no ownership or control
                (“Distributed Ledger”). You acknowledge and agree that,
                notwithstanding anything in these Terms to the contrary, in the
                event we do migrate all or any part of the Services to a
                Distributed Ledger, (i) your Eth.id domain(s) may be migrated to
                the Distributed Ledger in the form of a non-fungible token, (ii)
                if you elect to purchase or trade Eth.id domains from or with
                other users, any financial transactions that you engage in may
                be conducted solely through the Distributed Ledger, (iii) we
                will have no access to or control over these payments or
                transactions, nor will we have the ability to reverse any
                transactions, (iv) the Distributed Ledger may require the
                payment of a transaction fee (a “Transaction Fee”) for every
                transaction that occurs thereon (including, without limitation,
                each time you access, use, manage and/or modify your Eth.id
                domain or the Associated Data) and you may need to pay a
                Transaction Fee for each transaction that occurs via the
                Distributed Ledger, (v) in addition to the Transaction Fee, each
                time you utilize the Distributed Ledger to conduct a Eth.id
                domain transaction with another user via the Distributed Ledger,
                Company may collect a Transaction Fee on the total value of that
                transaction and the applicable Transaction Fee may be
                transferred directly to Company through the Distributed Ledger
                as part of the payment, (vi) Company will have no liability to
                you or to any third party for any claims or damages that may
                arise as a result of any transactions that you engage in via the
                Distributed Ledger and (vii) Company will have no further
                obligations to you under these Terms whatsoever and you shall
                look solely to the Distributed Ledger (and, in any event, not
                Company) for the receipt and enjoyment of all of your rights
                under these Terms (including, without limitation, the rights and
                license granted to you pursuant to Section 7 hereof).
              </li>
            </ol>
          </b>
        </li>
        <li>
          <p>
            <b>Additional Terms and Amendments</b>
          </p>
          <ol>
            <li>
              We may supply different or additional terms in relation to some of
              our Services, and those different or additional terms become part
              of your agreement with us if you use those Services. If there is a
              conflict between these Terms and the additional terms, the
              additional terms will control for that conflict.
            </li>
            <li>
              We may make changes to these Terms from time to time. If we make
              changes, we will provide you with notice of such changes, such as
              by sending an email, providing a notice through our Services or
              updating the date at the top of these Terms. Unless we say
              otherwise in our notice, the amended Terms will be effective
              immediately, and your continued use of our Services after we
              provide such notice will confirm your acceptance of the changes.
              If you do not agree to the amended Terms, you must stop using our
              Services.
            </li>
            <li>
              The current version of the license terms for Eth.id domains and
              Eth.id items may be found at{' '}
              <Link to="/terms">https://eth.id/terms</Link> and are hereby
              incorporated by reference. You acknowledge and agree that you have
              read and understand the License Terms and agree to be bound by its
              terms.
            </li>
          </ol>
        </li>
        <li>
          <p>
            <b>Severability</b>
          </p>
          <p>
            If any provision or part of a provision of these Terms is unlawful,
            void or unenforceable, that provision or part of the provision is
            deemed severable from these Terms and does not affect the validity
            and enforceability of any remaining provisions.
          </p>
        </li>
        <li>
          <p>
            <b>Miscellaneous</b>
          </p>
          <p>
            The failure of Company to exercise or enforce any right or provision
            of these Terms will not operate as a waiver of such right or
            provision. These Terms reflect the entire agreement between the
            parties relating to the subject matter hereof and supersede all
            prior agreements, representations, statements and understandings of
            the parties, whether express or implied. The section titles in these
            Terms are for convenience only and have no legal or contractual
            effect. Use of the word “including” will be interpreted to mean
            “including without limitation.” You may not assign your rights and
            obligations under these Terms without our express written consent.
            Our failure to exercise or enforce any right or provision of these
            Terms will not operate as a waiver of such right or provision. We
            will not be liable for any delay or failure to perform any
            obligation under these Terms where the delay or failure results from
            any cause beyond our reasonable control. Your access to or use of
            the Services does not create any form of partnership, joint venture
            or any other similar relationship between you and us. Except as
            otherwise provided herein, these Terms are intended solely for the
            benefit of the parties and are not intended to confer third-party
            beneficiary rights upon any other person or entity. You agree that
            communications and transactions between us may be conducted
            electronically.
          </p>
        </li>
      </ol>
    </div>
  );
}
